import { getConfig } from '../config';
import {
  type OrganizationBilling,
  type Plan,
  PlanKind,
  PlanStatus,
  SubscriptionPaymentCycle,
} from '../entity/api/organization-billing';
import { type OrganizationUser } from '../entity/api/organization-user';
import { PlanFeature } from '../entity/api/plan';
import { tNamespace } from '../services/translation';

const tBilling = tNamespace('app.billing.general');

export function convertPlanStrToKind(kindStr?: string): PlanKind {
  switch ((kindStr ?? '').toLowerCase()) {
    case 'lite':
      return PlanKind.Lite;
    case 'grow':
      return PlanKind.Grow;
    case 'scale':
      return PlanKind.Scale;
    case 'enterprise':
      return PlanKind.Enterprise;
    case 'legacy':
      return PlanKind.Legacy;
    default:
      return PlanKind.Custom;
  }
}

export function getPlanRank(kind: PlanKind): number {
  switch (kind) {
    case PlanKind.Lite:
      return 0;
    case PlanKind.Grow:
      return 1;
    case PlanKind.Scale:
      return 2;
    case PlanKind.Enterprise:
      return 3;
    // Legacy and Custom are unranked — callers should not switch to/from these via this flow
    default:
      return -1;
  }
}

export const enum SwitchKind {
  Upgrade = 'upgrade',
  Downgrade = 'downgrade',
  Same = 'same',
}

export function classifySwitch(
  current: { kind: PlanKind; paymentCycle: SubscriptionPaymentCycle },
  next: { kind: PlanKind; paymentCycle: SubscriptionPaymentCycle }
): SwitchKind {
  if (current.kind === next.kind && current.paymentCycle === next.paymentCycle) {
    return SwitchKind.Same;
  }

  const currentRank = getPlanRank(current.kind);
  const nextRank = getPlanRank(next.kind);

  // Moving to a higher tier is always an upgrade
  if (nextRank > currentRank) {
    return SwitchKind.Upgrade;
  }

  // Moving to a lower tier is always a downgrade
  if (nextRank < currentRank) {
    return SwitchKind.Downgrade;
  }

  // Same tier: monthly → yearly is an upgrade, yearly → monthly is a downgrade
  if (
    current.paymentCycle === SubscriptionPaymentCycle.Month &&
    next.paymentCycle === SubscriptionPaymentCycle.Year
  ) {
    return SwitchKind.Upgrade;
  }

  return SwitchKind.Downgrade;
}

export function isNotSubscribed(
  bill:
    | {
        providerSubscriptionId?: string | null;
        currentPlanStatus: PlanStatus;
      }
    | null
    | undefined
): boolean {
  if (bill == null) {
    return true;
  }
  return (
    bill.providerSubscriptionId == null ||
    bill.providerSubscriptionId.trim().length === 0 ||
    bill.currentPlanStatus === PlanStatus.NotSubscribed
  );
}

export function isOrgCancelled(bill?: OrganizationBilling): boolean {
  if (bill == null) {
    return true;
  }
  return bill.currentPlanStatus === PlanStatus.Cancelled;
}

export function isOrgCancelling(bill?: OrganizationBilling): boolean {
  if (bill == null) {
    return true;
  }
  return bill.currentPlanStatus === PlanStatus.Cancelling;
}

export function calculateMonthlyPrice(pricePerMonth: number, priceDiscount: number): number {
  return pricePerMonth * ((100 - priceDiscount) / 100);
}

export function hasOrgFeature(
  orgBill: OrganizationBilling | undefined | null,
  feature: PlanFeature
): boolean {
  switch (feature) {
    case PlanFeature.AiContentGap:
      return orgBill?.plan?.hasAiContentGap ?? false;
    case PlanFeature.AiMetrics:
      return orgBill?.plan?.hasAiMetrics ?? false;
    case PlanFeature.RefreshContentLight:
      return orgBill?.plan?.hasRefreshContentLight ?? false;
    case PlanFeature.RefreshContentMajor:
      return orgBill?.plan?.hasRefreshContentMajor ?? false;
    case PlanFeature.SupportingMaterials:
      return orgBill?.plan?.hasSupportingMaterials ?? false;
    case PlanFeature.EnabledArticles:
      return orgBill?.plan?.enabledArticles ?? orgBill?.features?.enabledArticles ?? false;
    case PlanFeature.EnabledProducts:
      return orgBill?.plan?.enabledProducts ?? orgBill?.features?.enabledProducts ?? false;
    case PlanFeature.EnabledCollections:
      return orgBill?.plan?.enabledCollections ?? orgBill?.features?.enabledCollections ?? false;
  }
}

export function canOrgAddTeamMembers(
  orgBill: OrganizationBilling | undefined | null,
  members: OrganizationUser[],
  neededCount: number
): boolean {
  return (orgBill?.plan?.maxTeamUserCount ?? 0) >= members.length + neededCount;
}

export function getPlanFeatures(plan: Plan): string[] {
  const features: string[] = [];

  // Content creations
  features.push(tBilling('contentCreationsPerMonth', { count: plan.baseCreditsPerMonth }));

  // Users
  if (getConfig().plans[plan.kind]?.areUsersUnlimited) {
    features.push(tBilling('usersUnlimited'));
  } else if (plan.maxTeamUserCount > 1) {
    features.push(tBilling('users', { count: plan.maxTeamUserCount }));
  } else {
    features.push(tBilling('user', { count: plan.maxTeamUserCount }));
  }

  // AI models
  if (plan.aiOverview != null && plan.aiOverview.length > 1) {
    features.push(tBilling('allAiModels'));
  } else {
    features.push(tBilling('openAiModel'));
  }

  return features;
}
